// Copyright © 2021 Move Closer
import { ref, computed, toRefs } from '@vue/composition-api';
import { defaultValidationClassMap, useHasErrors, useIsValid, useSizeClass, useSyncModel, useValidMarkerClass } from '../../_composables';
import { abstractBaseControlProps } from '../InputControl';
/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export const getAbstractCheckControlProps = () => ({
    ...abstractBaseControlProps,
    /**
     * Id of check control.
     */
    id: {
        type: String,
        required: false,
        default: null
    },
    /**
     * Label to display next to control.
     */
    label: {
        type: String,
        required: false,
        default: null
    },
    /**
     * Control's value, synced via `v-model`.
     */
    model: { required: true },
    /**
     * Determines whether the control allows selection of more than one option.
     */
    type: {
        type: String,
        required: false,
        default: 'checkbox'
    },
    /**
     * Array of the options available to select.
     */
    value: {
        required: false,
        default: true
    }
});
/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export const getAbstractCheckListProps = () => ({
    ...abstractBaseControlProps,
    /**
     * Control's value, synced via `v-model`.
     */
    model: { required: true },
    /**
     * Determines whether the control allows selection of more than one option.
     */
    multiple: {
        type: Boolean,
        required: false,
        default: false
    },
    /**
     * Array of the options available to select.
     */
    options: {
        type: Array,
        required: true
    }
});
/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export const useCheckControl = (props, ctx, sizeRegistry, validClassMap = defaultValidationClassMap) => {
    const { errors, valid, model, multiple, size } = toRefs(props);
    const checked = useSyncModel(model, ctx);
    const checkType = computed(() => multiple.value ? 'checkbox' : 'radio');
    const hasErrors = useHasErrors(errors);
    const isValid = useIsValid(hasErrors, valid);
    const sizeClass = useSizeClass(size, sizeRegistry);
    const validationClass = useValidMarkerClass(isValid, validClassMap);
    const collapse = {
        list: ref([]),
        toggleCollapse(key) {
            if (this.list.value.includes(key)) {
                this.list.value = this.list.value.filter((item) => item !== key);
            }
            else {
                this.list.value.push(key);
            }
        },
        isCollapsed(key) {
            return !this.list.value.includes(key);
        },
        clampLines(key) {
            return this.isCollapsed(key) ? 2 : 'auto';
        }
    };
    return { checked, hasErrors, sizeClass, checkType, validationClass, collapse };
};
